/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `Register` for the Caintra Connect frontend.
 * @module pages/Register
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import styles from "../styles/Register.module.css";
import Caintra_Logo from "../assets/Caintra_Logo.png";
import { CircularProgress, Backdrop } from "@mui/material";
import { ReactComponent as VerticalDivider } from "../assets/icons/Register/VerticalDivider.svg";
import { ReactComponent as HorizontalDivider } from "../assets/icons/Register/HorizontalDivider.svg";

import useRegisterForm from "./register/useRegisterForm.js";
import RegisterStep1 from "./register/RegisterStep1.jsx";
import RegisterStep2 from "./register/RegisterStep2.jsx";
import RegisterStep3 from "./register/RegisterStep3.jsx";
import RegisterFormActions from "./register/RegisterFormActions.jsx";

const Register = () => {
  const form = useRegisterForm();
  const {
    t,
    stepNumber,
    loading,
    error,
    isTablet,
    isMediumMobile,
    isSmallMobile,
    handleNavigateToLogin,
  } = form;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <LanguageSelector />
      </div>
      <div className={styles.main}>
        <div className={styles.logoContainer}>
          <img src={Caintra_Logo} alt="" />
        </div>
        <VerticalDivider className={styles.verticalDivider} />
        <div className={styles.formContainer}>
          <div className={styles.formContent}>
            <div className={styles.title}>{t("pages.register.header")}</div>
            <div className={styles.subtitle}>
              {t("pages.register.subHeaderText1")}{" "}
              <span
                className={styles.registerLink}
                onClick={handleNavigateToLogin}
              >
                {t("pages.register.subHeaderText2")}
              </span>
            </div>
            {isTablet || isMediumMobile || isSmallMobile ? (
              <HorizontalDivider className={styles.horizontalDivider} />
            ) : null}

            {stepNumber === 1 && <RegisterStep1 {...form} />}
            {stepNumber === 2 && <RegisterStep2 {...form} />}
            {stepNumber === 3 && <RegisterStep3 {...form} />}

            <RegisterFormActions
              t={t}
              stepNumber={stepNumber}
              error={error}
              isStep1Valid={form.isStep1Valid}
              isStep2Valid={form.isStep2Valid}
              isStep3Valid={form.isStep3Valid}
              handleNextFromStep1={form.handleNextFromStep1}
              handleNextFromStep2={form.handleNextFromStep2}
              handlePreviousFromStep2={form.handlePreviousFromStep2}
              handlePreviousFromStep3={form.handlePreviousFromStep3}
              handleCreateAccount={form.handleCreateAccount}
            />
          </div>
        </div>
      </div>

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={loading}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: "#4eabe7",
          }}
        />
      </Backdrop>
    </div>
  );
};

export default Register;
